import { gql } from '@apollo/client';
import { getOneQueryLite } from '@xbcb/shared-queries';
import { client as apolloClient } from '@xbcb/apollo-client';
import { RecordType } from '@xbcb/shared-types';
import { get } from 'lodash';
import { IMPORTER_FIELDS_FRAGMENT } from 'components/ImporterDetailPage/queries.query';

const fragments = gql`
  fragment recordActionFields on RecordAction {
    source {
      __typename
      ... on InternalSystem {
        internalSystemName: name
      }
      ... on InternalWebsite {
        internalWebsiteName: name
      }
      ... on ClientSystem {
        clientSystemName: name
      }
    }
    time
    reason {
      code
      text
    }
    client {
      id
    }
    request {
      ipAddress
      userAgent
      requestId
    }
  }

  fragment recordFields on Record {
    created {
      ...recordActionFields
    }
    updated {
      ...recordActionFields
    }
    deleted {
      ...recordActionFields
    }
    id
    version
    tags {
      key
      value
    }
  }

  fragment usIorFields on UsIor {
    id
    version
    status
    industry
    cbpStatus
    cbp5106RejectionReasons {
      code
      name
    }
    updated {
      time
    }
    businessStructure
    activationStatus
    activations {
      status
      customsBroker {
        id
      }
    }
    iorNumber {
      value
    }
    payerUnitNumber
    docReadinessId
    bondApplicationInformation {
      productDescription
      importsFrom
      expectedDutiesRange
      previousAnnualFeesRange
      customBondApplicationStatus
      supplementaryDutyFlagType
    }
    continuousBonds {
      present {
        amount
        effectiveDate
        number
        suretyCode
      }
    }
    documents {
      id
      deleted {
        time
      }
      tags {
        key
        value
      }
      documentTags
    }
    addresses {
      mailing {
        address
        countryCode
        postalCode
        city
        stateCode
      }
      physical {
        address
        countryCode
        postalCode
        city
        stateCode
      }
    }
    addressList {
      cityName
      country
      countrySubDivision
      countrySubDivisionName
      houseNumber
      houseNumberAddition
      line
      postCode
      postOfficeBox
      streetName
      type
    }
    identifiers {
      identification
      identificationType
    }
    isResident
    pointOfContact {
      email
      name
      phone {
        number
        extension
        country
      }
      title
    }
    operator {
      id
    }
    dpsRecord {
      id
      version
      overallDpsStatus
      dpsStatus {
        code
        time
      }
    }
    tags {
      key
      value
    }
    created {
      time
    }
    memos {
      id
      version
      text
      isPublic
      created {
        time
        client {
          id
          name
        }
      }
      updated {
        time
        client {
          id
          name
        }
      }
      deleted {
        time
      }
    }
  }
`;

export const queryImporterEntityActivation = gql`
  ${IMPORTER_FIELDS_FRAGMENT}
  query ImporterEntityActivation($id: ID!) {
    importerEntityActivation(id: $id) {
      id
      version
      status
      deadline
      operator {
        id
      }
      broker {
        id
      }
      milestones {
        tags {
          key
          value
        }
        id
        name
        occurenceTime
        source
        created {
          client {
            name
          }
        }
      }
      inquiries {
        reasonCodes
        clarifyDocuments {
          id
          fileName
          extension
        }
        attachDocuments {
          id
          fileName
          extension
        }
        requestDocuments
        milestone {
          id
        }
        description
        task {
          id
        }
      }
      tasks {
        ...recordFields
        name
        workOrderType
        status
        assignee {
          id
          name
        }
        assignmentTeam {
          id
          name
          backupTeam {
            id
            name
          }
        }
        deadline
        steps {
          id
          name
          description
          isCompletedBySystem
          isFinalConfirmation
          completed {
            ...recordActionFields
          }
        }
        definition {
          id
          estimatedDuration
          workOrderTaskType
        }
      }
      group {
        documents {
          documentTags
        }
        customsBroker {
          tags {
            key
            value
          }
        }
        forwarders {
          forwarder {
            id
            name
            billingDetails {
              disbursement
              status
              subscriptionPlan
            }
          }
        }
        shipment {
          id
          clientReferences {
            bookingId
          }
          legs {
            transportationServiceName
          }
          preferredDutyCollectPartyType
        }
        invoices {
          id
        }
        id
        shipper {
          id
          shipperExternallySourcedDetails {
            externalReferenceId
          }
          billingDetails {
            disbursement
            status
            subscriptionPlan
          }
        }
      }
      countryCode
      usIor {
        ...usIorFields
        importer {
          ...ImporterFields
          usIor {
            ...usIorFields
          }
        }
      }
      importerEntity {
        id
        version
        addressList {
          cityName
          country
          countrySubDivision
          countrySubDivisionName
          externalReference {
            id
            customerId
          }
          houseNumber
          houseNumberAddition
          line
          postCode
          postOfficeBox
          streetName
          type
        }
        updated {
          time
        }
        countryCode
        bonds {
          present {
            referenceIdentifier
            typeCode
            amount
            effectiveDate
            renewalDate
            terminationDate
            surety {
              name
              code
            }
          }
          past {
            referenceIdentifier
            typeCode
            amount
            effectiveDate
            renewalDate
            terminationDate
            surety {
              name
              code
            }
          }
          future {
            referenceIdentifier
            typeCode
            amount
            effectiveDate
            renewalDate
            terminationDate
            surety {
              name
              code
            }
          }
        }
        isResident
        status
        identifiers {
          identification
          identificationType
        }
        program {
          docReadiness {
            id
          }
        }
        pointOfContact {
          email
          name
          phone {
            number
            extension
            country
          }
          title
          user {
            id
            name
          }
        }
        activations {
          activated {
            time
          }
          status
          externalIorReferences {
            id
            referenceType
            version
            documents {
              documentTag
              externalReference {
                externalReferenceType
                externalReferencePartition
                id
                version
              }
            }
          }
          customsBroker {
            id
          }
        }
        externalClientReferences
        dpsRecord {
          id
          version
          overallDpsStatus
          dpsStatus {
            code
            time
          }
        }
        externalValidations {
          response {
            validationResult
            companyDetails {
              name
            }
          }
          identification {
            identificationType
          }
          validationDate
        }
        importer {
          ...ImporterFields
          importerEntities {
            nodes {
              id
              version
              addressList {
                cityName
                country
                countrySubDivision
                countrySubDivisionName
                externalReference {
                  id
                  customerId
                }
                houseNumber
                houseNumberAddition
                line
                postCode
                postOfficeBox
                streetName
                type
              }
              updated {
                time
              }
              countryCode
              bonds {
                present {
                  referenceIdentifier
                  typeCode
                  amount
                  effectiveDate
                  renewalDate
                  terminationDate
                  surety {
                    name
                    code
                  }
                }
                past {
                  referenceIdentifier
                  typeCode
                  amount
                  effectiveDate
                  renewalDate
                  terminationDate
                  surety {
                    name
                    code
                  }
                }
                future {
                  referenceIdentifier
                  typeCode
                  amount
                  effectiveDate
                  renewalDate
                  terminationDate
                  surety {
                    name
                    code
                  }
                }
              }
              isResident
              status
              identifiers {
                identification
                identificationType
              }
              program {
                docReadiness {
                  id
                }
              }
              pointOfContact {
                email
                name
                phone {
                  number
                  extension
                  country
                }
                title
              }
              dpsRecord {
                id
                version
                overallDpsStatus
                dpsStatus {
                  code
                  time
                }
              }
              externalValidations {
                response {
                  validationResult
                  companyDetails {
                    name
                  }
                }
                identification {
                  identificationType
                }
                validationDate
              }
            }
          }
        }
      }
    }
  }
  ${fragments}
`;

export const updateImporterEntityActivation = gql`
  mutation UpdateImporterEntityActivation(
    $id: ID!
    $version: Int!
    $input: UpdateImporterEntityActivationInput!
  ) {
    updateImporterEntityActivation(input: $input, version: $version, id: $id) {
      record {
        id
        version
      }
    }
  }
`;

export const upsertImporterEntityActivationMutation = () => {
  return gql`
    mutation upsertImporterEntityActivation(
      $id: ID!
      $version: Int!
      $input: UpsertImporterEntityActivationInput!
    ) {
      upsertImporterEntityActivation(
        id: $id
        version: $version
        input: $input
      ) {
        importerEntity {
          id
          version
        }
      }
    }
  `;
};

export const getImporterEntityVersion = async (importerEntityId: string) => {
  const importerEntityVersionQuery = getOneQueryLite({
    recordName: RecordType.IMPORTER_ENTITY,
    fields: 'version',
    queryName: `getImporterEntityVersion`,
  });

  const { data } = await apolloClient.query({
    query: importerEntityVersionQuery,
    variables: { id: importerEntityId },
    errorPolicy: 'all',
  });
  const version = get(data, [`importerEntity`, `version`]);
  return version;
};

export const getUsIorVersion = async (usIorId: string) => {
  const importerEntityVersionQuery = getOneQueryLite({
    recordName: RecordType.US_IOR,
    fields: 'version',
    queryName: `getUsIorVersion`,
  });

  const { data } = await apolloClient.query({
    query: importerEntityVersionQuery,
    variables: { id: usIorId },
    errorPolicy: 'all',
  });
  const version = get(data, [`usIor`, `version`]);
  return version;
};
